import './survey.scss';
import Header from '../../../organisms/header/header.js';
import RoleRibbonByRole from '../../../organisms/common/role-ribbon/role-ribbon-by-role';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import decodeToken from '../../../../utils/decode-token';
import apiClient from '../../../../utils/apiClient';
import Checkbox from '@mui/material/Checkbox';
import Radio from '@mui/material/Radio';
import FormControlLabel from '@mui/material/FormControlLabel';
import Collapse from '@mui/material/Collapse';
import { isAdminRole } from '../../../../utils/role-utils';
import BackButton from '../../../atoms/back-button';

function flattenObject(obj, prefix = '', result = {}) {
  Object.entries(obj || {}).forEach(([key, value]) => {
    const nextKey = prefix ? `${prefix}.${key}` : key;
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      flattenObject(value, nextKey, result);
    } else {
      result[nextKey] = value;
    }
  });
  return result;
}

function getTranslationsMap(i18n) {
  const lang = i18n.resolvedLanguage || i18n.language;
  const languageData = i18n.store?.data?.[lang] || {};
  const merged = {};

  Object.values(languageData).forEach((namespaceData) => {
    const flat = flattenObject(namespaceData || {});
    Object.entries(flat).forEach(([key, value]) => {
      merged[key] = value;
    });
  });

  return merged;
}

function extractId(key) {
  const match = String(key || '').match(/-id-?(\d+)(?:-closing)?$/i);
  return match ? Number(match[1]) : null;
}

function parseQuestionKey(key, surveyId) {
  const regex = new RegExp(`^survey${surveyId}q(\\d+)[_.](\\d+)(.*)$`, 'i');
  const match = String(key || '').match(regex);

  if (!match) return null;

  const stepNumber = Number(match[1]);
  const questionNumber = Number(match[2]);
  const suffix = match[3] || '';

  if (/^-\d+$/.test(suffix)) return null;

  return {
    stepNumber,
    questionNumber,
    suffix
  };
}

function getQuestionTypeFromKey(key) {
  const normalized = String(key || '').toLowerCase();

  if (normalized.includes('text')) return 'text';
  if (normalized.includes('multi')) return 'multi';
  return 'radio';
}

function isQuestionTranslationKey(key, surveyId) {
  const normalized = String(key || '').toLowerCase();
  const questionBase = new RegExp(`^survey${surveyId}q\\d+[_.]\\d+`, 'i');

  if (!questionBase.test(key)) return false;
  if (!normalized.includes('-id')) return false;
  if (/-\d+-+id-?\d+(?:-closing)?$/i.test(key)) return false;

  return true;
}

function parseOptionTranslationKey(key, surveyId) {
  const value = String(key || '');

  const patterns = [
    new RegExp(`^survey${surveyId}(?:a|q)(\\d+)[_.](\\d+)-(\\d+)-+id-?(\\d+)$`, 'i'),
    new RegExp(`^survey${surveyId}(?:a|q)(\\d+)[_.](\\d+)-(\\d+)$`, 'i')
  ];

  for (const pattern of patterns) {
    const match = value.match(pattern);
    if (match) {
      return {
        stepNumber: Number(match[1]),
        questionNumber: Number(match[2]),
        optionOrder: Number(match[3]),
        optionId: match[4] ? Number(match[4]) : null
      };
    }
  }

  return null;
}

function buildInitialAnswer(question) {
  if (question.type === 'text') {
    return { text: '' };
  }

  if (question.type === 'multi') {
    return { selected: [], otherSelected: false, otherText: '', otherOptionId: null };
  }

  return { value: '', otherSelected: false, otherText: '', otherOptionId: null };
}

function isQuestionAnswered(answer, question) {
  if (!answer) return false;

  if (question.type === 'text') {
    return typeof answer.text === 'string' && answer.text.trim() !== '';
  }

  if (question.type === 'multi') {
    const selected = Array.isArray(answer.selected) ? answer.selected : [];
    const selectedWithoutOther = answer.otherOptionId
      ? selected.filter((id) => id !== answer.otherOptionId)
      : selected;

    if (!selectedWithoutOther.length && !answer.otherSelected) return false;
    if (answer.otherSelected) {
      return typeof answer.otherText === 'string' && answer.otherText.trim() !== '';
    }
    return true;
  }

  if (answer.otherSelected) {
    return typeof answer.otherText === 'string' && answer.otherText.trim() !== '';
  }

  return answer.value !== undefined && answer.value !== null && answer.value !== '';
}

function getNextStepFromAnswers(answersMap, survey) {
  if (!survey.steps.length) return 1;

  for (const step of survey.steps) {
    const isStepComplete = step.questions.every((question) =>
      isQuestionAnswered(answersMap[question.key], question)
    );

    if (!isStepComplete) {
      return step.stepNumber;
    }
  }

  return survey.steps.length < 3
    ? survey.steps[survey.steps.length - 1].stepNumber
    : Math.min(survey.steps[survey.steps.length - 1].stepNumber, survey.steps.length);
}

function buildSurveyStructure(translations, surveyId) {
  const title = translations[`survey${surveyId}-title`] || '';
  const closingText = translations[`survey${surveyId}-closing`] || '';

  const stepTitles = {};
  const questionsMap = {};
  const closingQuestions = [];
  const optionsByQuestionKey = {};

  Object.entries(translations).forEach(([key, value]) => {
    const sectionMatch = key.match(new RegExp(`^survey${surveyId}-section(\\d+)-title$`, 'i'));
    if (sectionMatch) {
      stepTitles[Number(sectionMatch[1])] = value;
      return;
    }

    const optionMeta = parseOptionTranslationKey(key, surveyId);
    if (optionMeta) {
      const questionKey = `q${optionMeta.stepNumber}_${optionMeta.questionNumber}`;
      if (!optionsByQuestionKey[questionKey]) {
        optionsByQuestionKey[questionKey] = [];
      }

      const isOtherByLabel = typeof value === 'string' && value.toLowerCase().includes('other');

      optionsByQuestionKey[questionKey].push({
        key: `${questionKey}-option-${optionMeta.optionOrder}`,
        order: optionMeta.optionOrder,
        optionId: optionMeta.optionId ?? optionMeta.optionOrder,
        label: value,
        isOther: isOtherByLabel,
        sourceKey: key
      });
      return;
    }

    if (!isQuestionTranslationKey(key, surveyId)) return;

    const parsed = parseQuestionKey(key, surveyId);
    if (!parsed) return;

    const normalizedKey = key.toLowerCase();
    const question = {
      key: `q${parsed.stepNumber}_${parsed.questionNumber}`,
      stepNumber: parsed.stepNumber,
      questionNumber: parsed.questionNumber,
      questionId: extractId(key),
      sourceKey: key,
      label: value,
      type: getQuestionTypeFromKey(key),
      isBoolean: normalizedKey.includes('boolean'),
      hasOther: normalizedKey.includes('other'),
      isClosing: normalizedKey.includes('closing'),
      options: []
    };

    if (question.isClosing) {
      closingQuestions.push(question);
    } else {
      questionsMap[question.key] = question;
    }
  });

  [...Object.values(questionsMap), ...closingQuestions].forEach((question) => {
    if (question.type === 'text') return;

    const options = (optionsByQuestionKey[question.key] || [])
      .sort((a, b) => a.order - b.order)
      .map((option) => {
        const resolvedOptionId = question.isBoolean
          ? null
          : extractId(option.sourceKey) ?? option.optionId;

        const resolvedValue = question.isBoolean
          ? option.order === 1
          : resolvedOptionId;

        return {
          ...option,
          optionId: resolvedOptionId,
          value: resolvedValue
        };
      });

    if (question.hasOther && options.length > 0 && !options.some((option) => option.isOther)) {
      question.options = options.map((option, index) => ({
        ...option,
        isOther: index === options.length - 1
      }));
    } else {
      question.options = options;
    }
  });

  const stepNumbers = Array.from(
    new Set(Object.values(questionsMap).map((question) => question.stepNumber))
  ).sort((a, b) => a - b);

  const steps = stepNumbers.map((stepNumber) => ({
    stepNumber,
    title: stepTitles[stepNumber] || '',
    questions: Object.values(questionsMap)
      .filter((question) => question.stepNumber === stepNumber)
      .sort((a, b) => a.questionNumber - b.questionNumber)
  }));

  return {
    title,
    closingText,
    closingQuestions: closingQuestions.sort((a, b) => {
      if (a.stepNumber !== b.stepNumber) return a.stepNumber - b.stepNumber;
      return a.questionNumber - b.questionNumber;
    }),
    steps
  };
}

function buildAnswersFromApi(apiAnswers, survey) {
  const normalizedApiAnswers = Array.isArray(apiAnswers)
    ? apiAnswers
    : apiAnswers
      ? [apiAnswers]
      : [];

  const allQuestions = [
    ...survey.steps.flatMap((step) => step.questions),
    ...survey.closingQuestions
  ];

  const questionsById = new Map(
    allQuestions
      .filter((question) => question.questionId !== null && question.questionId !== undefined)
      .map((question) => [question.questionId, question])
  );

  const hydratedAnswers = {};

  normalizedApiAnswers.forEach((apiAnswer) => {
    const question = questionsById.get(apiAnswer.question_id);
    if (!question) return;

    if (question.type === 'text') {
      hydratedAnswers[question.key] = {
        text: apiAnswer.value ?? apiAnswer.other_text ?? ''
      };
      return;
    }

    if (question.type === 'multi') {
      const selectedIds = Array.isArray(apiAnswer.option_ids) ? apiAnswer.option_ids : [];
      const otherOption = question.options.find((option) => option.isOther);
      const otherSelected = otherOption ? selectedIds.includes(otherOption.optionId) : false;

      hydratedAnswers[question.key] = {
        selected: selectedIds,
        otherSelected,
        otherText: apiAnswer.other_text ?? '',
        otherOptionId: otherSelected ? otherOption?.optionId ?? null : null
      };
      return;
    }

    if (question.isBoolean) {
      hydratedAnswers[question.key] = {
        value: apiAnswer.value,
        otherSelected: false,
        otherText: apiAnswer.other_text ?? '',
        otherOptionId: null
      };
      return;
    }

    const otherOption = question.options.find((option) => option.isOther);
    const selectedOptionId = apiAnswer.option_id ?? null;
    const isOtherSelected = otherOption ? selectedOptionId === otherOption.optionId : false;

    hydratedAnswers[question.key] = {
      value: isOtherSelected ? '' : selectedOptionId,
      otherSelected: isOtherSelected,
      otherText: apiAnswer.other_text ?? '',
      otherOptionId: isOtherSelected ? otherOption?.optionId ?? null : null
    };
  });

  return hydratedAnswers;
}

function buildApiAnswer(question, answer, mode = 'default') {
  if (!question?.questionId) return null;

  const questionId = extractId(question.sourceKey) ?? question.questionId;

  if (question.type === 'text') {
    return {
      question_id: questionId,
      value: answer?.text ?? ''
    };
  }

  if (question.type === 'multi') {
    const otherOptionId = answer?.otherOptionId ?? null;
    const selected = Array.isArray(answer?.selected) ? answer.selected : [];
    const selectedWithoutOther = otherOptionId
      ? selected.filter((id) => id !== otherOptionId)
      : selected;

    if (mode === 'multi-other') {
      if (!answer?.otherSelected || !otherOptionId) return null;
      return {
        question_id: questionId,
        option_id: otherOptionId,
        other_text: answer?.otherText ?? ''
      };
    }

    return {
      question_id: questionId,
      option_ids: selectedWithoutOther
    };
  }

  if (question.isBoolean) {
    return {
      question_id: questionId,
      value: answer?.value === true
    };
  }

  if (answer?.otherSelected) {
    return {
      question_id: questionId,
      option_id: answer?.otherOptionId ?? null,
      other_text: answer?.otherText ?? ''
    };
  }

  return {
    question_id: questionId,
    option_id: answer?.value ?? null
  };
}

function formatCurrentDateTime() {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, '0');

  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
}

function FlatSurvey({ surveyId, hacId, isReadOnly = false }) {
  const { i18n } = useTranslation();
  const { t } = useTranslation();

  const translations = useMemo(
    () => getTranslationsMap(i18n),
    [i18n, i18n.language, i18n.resolvedLanguage]
  );

  const survey = useMemo(() => {
    if (!surveyId) {
      return {
        title: '',
        closingText: '',
        closingQuestions: [],
        steps: []
      };
    }

    return buildSurveyStructure(translations, surveyId);
  }, [translations, surveyId]);

  const [answers, setAnswers] = useState({});
  const [rawApiAnswers, setRawApiAnswers] = useState([]);
  const [submissionId, setSubmissionId] = useState(null);
  const [submissionStatus, setSubmissionStatus] = useState('in_progress');
  const [submittedAt, setSubmittedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  const isSubmitted = submissionStatus === 'submitted';

  useEffect(() => {
    const initialExpandedState = {};
    survey.steps.forEach((step) => {
      initialExpandedState[`step-${step.stepNumber}`] = false;
    });
    setExpandedSections(initialExpandedState);
  }, [survey]);

  const toggleSection = (sectionKey) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  useEffect(() => {
    setAnswers({});
    setRawApiAnswers([]);
    setSubmissionId(null);
    setSubmissionStatus('in_progress');
    setSubmittedAt(null);
    setSubmitting(false);
    setLoading(true);
  }, [surveyId, hacId]);

  useEffect(() => {
    if (!surveyId || !hacId) {
      setLoading(false);
      return;
    }

    const fetchAnswers = async () => {
      try {
        setLoading(true);

        const response = await apiClient.get(
          `${process.env.REACT_APP_API_URL}/surveys/${surveyId}/answers?hacId=${hacId}`
        );

        const responseData = response.data;
        const responseAnswers = Array.isArray(responseData)
          ? responseData
          : Array.isArray(responseData?.answers)
            ? responseData.answers
            : responseData
              ? [responseData]
              : [];

        if (responseData?.submission_id) {
          setSubmissionId(responseData.submission_id);
        }

        if (responseData?.status) {
          setSubmissionStatus(responseData.status);
        }

        if (responseData?.submitted_at) {
          setSubmittedAt(responseData.submitted_at);
        }

        setRawApiAnswers(responseAnswers);
      } catch (error) {
        console.error('Error fetching survey answers:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnswers();
  }, [surveyId, hacId]);

  useEffect(() => {
    const hasStructure = survey.steps.length > 0 || survey.closingQuestions.length > 0;
    if (!hasStructure) return;

    const hydratedAnswers = buildAnswersFromApi(rawApiAnswers, survey);
    setAnswers(hydratedAnswers);

    const firstIncompleteStep = getNextStepFromAnswers(hydratedAnswers, survey);
    const nextExpanded = {};
    survey.steps.forEach((step) => {
      nextExpanded[`step-${step.stepNumber}`] = step.stepNumber === firstIncompleteStep;
    });
    setExpandedSections(nextExpanded);
  }, [rawApiAnswers, survey]);

  const postAnswerUpdate = useCallback(async (question, nextAnswer, mode = 'default') => {
    if (!surveyId || !hacId || !question?.questionId || isSubmitted || isReadOnly) return;

    const apiAnswer = buildApiAnswer(question, nextAnswer, mode);
    if (!apiAnswer) return;

    if (question.type === 'multi' && mode === 'default' && (!apiAnswer.option_ids || !apiAnswer.option_ids.length)) {
      return;
    }

    try {
      await apiClient.post(
        `${process.env.REACT_APP_API_URL}/surveys/${surveyId}/answers?hacId=${hacId}`,
        apiAnswer
      );
    } catch (error) {
      console.error('Error saving survey answer:', error);
    }
  }, [surveyId, hacId, isSubmitted, isReadOnly]);

  const submitSurvey = useCallback(async () => {
    if (!surveyId || !hacId || !submissionId || isSubmitted || isReadOnly) return;

    const submittedAtFormatted = formatCurrentDateTime();

    const payload = {
      ok: true,
      submission_id: submissionId,
      status: 'submitted',
      submitted_at: submittedAtFormatted
    };

    try {
      setSubmitting(true);

      const response = await apiClient.post(
        `${process.env.REACT_APP_API_URL}/surveys/${surveyId}/submit?hacId=${hacId}`,
        payload
      );

      const responseData = response.data;

      setSubmissionId(responseData?.submission_id ?? submissionId);
      setSubmissionStatus(responseData?.status ?? 'submitted');
      setSubmittedAt(responseData?.submitted_at ?? submittedAtFormatted);
    } catch (error) {
      console.error('Error submitting survey:', error);
    } finally {
      setSubmitting(false);
    }
  }, [surveyId, hacId, submissionId, isSubmitted, isReadOnly]);

  const allQuestions = useMemo(() => ([
    ...survey.steps.flatMap((step) => step.questions),
    ...survey.closingQuestions
  ]), [survey]);

  const allAnswered = useMemo(() => {
    if (!allQuestions.length) return false;
    return allQuestions.every((question) => isQuestionAnswered(answers[question.key], question));
  }, [allQuestions, answers]);

  const handleTextChange = (question, value) => {
    if (isSubmitted || isReadOnly) return;

    const nextAnswer = {
      ...(answers[question.key] || buildInitialAnswer({ type: 'text' })),
      text: value
    };

    setAnswers((prev) => ({
      ...prev,
      [question.key]: nextAnswer
    }));
  };

  const handleTextBlur = async (question) => {
    if (isReadOnly) return;
    const nextAnswer = answers[question.key] || buildInitialAnswer({ type: 'text' });
    await postAnswerUpdate(question, nextAnswer);
  };

  const handleRadioChange = async (question, option) => {
    if (isSubmitted || isReadOnly) return;

    const previous = answers[question.key] || buildInitialAnswer({ type: 'radio' });

    const nextAnswer = option.isOther
      ? {
        ...previous,
        value: '',
        otherSelected: true,
        otherText: previous.otherText || '',
        otherOptionId: option.optionId
      }
      : question.isBoolean
        ? {
          ...previous,
          value: option.value,
          otherSelected: false,
          otherText: '',
          otherOptionId: null
        }
        : {
          ...previous,
          value: option.optionId,
          otherSelected: false,
          otherText: '',
          otherOptionId: null
        };

    setAnswers((prev) => ({
      ...prev,
      [question.key]: nextAnswer
    }));

    await postAnswerUpdate(question, nextAnswer);
  };

  const handleRadioOtherTextChange = (question, option, value) => {
    if (isSubmitted || isReadOnly) return;

    const previous = answers[question.key] || buildInitialAnswer({ type: 'radio' });

    const nextAnswer = {
      ...previous,
      otherText: value,
      otherOptionId: option.optionId
    };

    setAnswers((prev) => ({
      ...prev,
      [question.key]: nextAnswer
    }));
  };

  const handleRadioOtherTextBlur = async (question) => {
    if (isReadOnly) return;
    const nextAnswer = answers[question.key] || buildInitialAnswer({ type: 'radio' });
    if (!nextAnswer.otherSelected) return;
    await postAnswerUpdate(question, nextAnswer);
  };

  const handleMultiChange = async (question, option, checked) => {
    if (isSubmitted || isReadOnly) return;

    const previous = answers[question.key] || buildInitialAnswer({ type: 'multi' });
    const selected = Array.isArray(previous.selected) ? previous.selected : [];
    const resolvedOptionId = option.optionId;

    const nextSelected = checked
      ? [...selected.filter((item) => item !== resolvedOptionId), resolvedOptionId]
      : selected.filter((item) => item !== resolvedOptionId);

    const nextAnswer = option.isOther
      ? {
        ...previous,
        selected: nextSelected,
        otherSelected: checked,
        otherText: checked ? previous.otherText || '' : '',
        otherOptionId: checked ? resolvedOptionId : null
      }
      : {
        ...previous,
        selected: nextSelected
      };

    setAnswers((prev) => ({
      ...prev,
      [question.key]: nextAnswer
    }));

    if (!option.isOther) {
      await postAnswerUpdate(question, nextAnswer, 'default');
    }
  };

  const handleMultiOtherTextChange = (question, option, value) => {
    if (isSubmitted || isReadOnly) return;

    const previous = answers[question.key] || buildInitialAnswer({ type: 'multi' });

    const nextAnswer = {
      ...previous,
      otherText: value,
      otherOptionId: option.optionId
    };

    setAnswers((prev) => ({
      ...prev,
      [question.key]: nextAnswer
    }));
  };

  const handleMultiOtherTextBlur = async (question) => {
    if (isReadOnly) return;
    const nextAnswer = answers[question.key] || buildInitialAnswer({ type: 'multi' });
    if (!nextAnswer.otherSelected) return;
    await postAnswerUpdate(question, nextAnswer, 'multi-other');
  };

  const handleSubmit = async () => {
    if (isSubmitted || isReadOnly) return;
    if (!allAnswered) return;
    await submitSurvey();
  };

  const renderQuestion = (question, sectionKey) => {
    const answer = answers[question.key] || buildInitialAnswer(question);

    if (question.type === 'text') {
      return (
        <div className="textarea required" key={`${sectionKey}-${question.key}-textarea`}>
          <p>
            <strong>
              {question.label} <span>*</span>
            </strong>
          </p>
          <div>
            <div className="form-control">
              <textarea
                className="txt-area"
                name={question.key}
                maxLength={100}
                value={answer.text || ''}
                onChange={(e) => handleTextChange(question, e.target.value)}
                onBlur={() => handleTextBlur(question)}
                disabled={isSubmitted || isReadOnly}
              />
            </div>
          </div>
        </div>
      );
    }

    if (question.type === 'multi') {
      return (
        <div className="checkbox required" key={`${sectionKey}-${question.key}-checkbox`}>
          <p>
            <strong>
              {question.label} <span>*</span>
            </strong>
          </p>
          {question.options.map((option) => {
            const checked = Array.isArray(answer.selected) && answer.selected.includes(option.optionId);
            const inputId = `${sectionKey}-q${question.questionNumber}-${option.order}`;

            return (
              <div key={`${sectionKey}-${question.key}-${option.order}`}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id={inputId}
                      checked={checked}
                      onChange={(e) => handleMultiChange(question, option, e.target.checked)}
                      value={option.optionId}
                      disabled={isSubmitted || isReadOnly}
                    />
                  }
                  label={option.label}
                />
                {option.isOther && answer.otherSelected && answer.otherOptionId === option.optionId && (
                  <textarea
                    className="txt-area"
                    name={`${question.key}-other`}
                    maxLength={100}
                    value={answer.otherText || ''}
                    onChange={(e) => handleMultiOtherTextChange(question, option, e.target.value)}
                    onBlur={() => handleMultiOtherTextBlur(question)}
                    disabled={isSubmitted || isReadOnly}
                  />
                )}
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <div className="radio required" key={`${sectionKey}-${question.key}-radio`}>
        <p>
          <strong>
            {question.label} <span>*</span>
          </strong>
        </p>
        {question.options.map((option) => {
          const checked = question.isBoolean
            ? answer.value === option.value
            : option.isOther
              ? answer.otherSelected && answer.otherOptionId === option.optionId
              : answer.value === option.optionId;

          const inputId = `${sectionKey}-q${question.questionNumber}-${option.order}`;

          return (
            <div key={`${sectionKey}-${question.key}-${option.order}`}>
              <FormControlLabel
                control={
                  <Radio
                    id={inputId}
                    checked={checked}
                    onChange={() => handleRadioChange(question, option)}
                    value={question.isBoolean ? String(option.value) : option.optionId}
                    name={question.key}
                    disabled={isSubmitted || isReadOnly}
                  />
                }
                label={option.label}
              />
              {option.isOther && answer.otherSelected && answer.otherOptionId === option.optionId && (
                <textarea
                  className="txt-area"
                  name={`${question.key}-other`}
                  maxLength={100}
                  value={answer.otherText || ''}
                  onChange={(e) => handleRadioOtherTextChange(question, option, e.target.value)}
                  onBlur={() => handleRadioOtherTextBlur(question)}
                  disabled={isSubmitted || isReadOnly}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return <div className="survey-content">{t('common-loading')}</div>;
  }

  if (!survey.steps.length && !survey.closingQuestions.length) {
    return <div className="survey-content">No survey structure available.</div>;
  }

  return (
    <div className="survey-content">
      {survey.title && <h2>{survey.title}</h2>}

      {survey.steps.map((step) => {
        const sectionKey = `step-${step.stepNumber}`;
        const isExpanded = expandedSections[sectionKey];

        return (
          <div key={sectionKey} className="survey-section">
            <div className="title" onClick={() => toggleSection(sectionKey)}>
              <h5 className="step-title">
                {step.stepNumber}. {step.title}
              </h5>
              <button
                type="button"
                className="btn text"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleSection(sectionKey);
                }}
              >
                {isExpanded ? (
                  <span className="icon-minus"></span>
                ) : (
                  <span className="icon-plus"></span>
                )}
              </button>
            </div>

            <Collapse in={!!isExpanded}>
              <div className="questions-list">
                {step.questions.map((question) => renderQuestion(question, `step${step.stepNumber}`))}
              </div>
            </Collapse>
          </div>
        );
      })}

      {(survey.closingText || survey.closingQuestions.length > 0) && (
        <div className="survey-section">
          {survey.closingText && <p>{survey.closingText}</p>}
          <div className="questions-list">
            {survey.closingQuestions.map((question) => renderQuestion(question, 'closing'))}
          </div>
        </div>
      )}

      {!isSubmitted && !isReadOnly && (
        <div className="actions">
          <button className="btn" onClick={handleSubmit} disabled={!allAnswered || submitting}>
            {submitting ? '' : t('common-submit')}
          </button>
        </div>
      )}

      {submittedAt && (
        <div className="actions">
          <p>{`${submittedAt}`}</p>
        </div>
      )}
    </div>
  );
}

function AdminSurvey() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'auto'
    });
  }, []);

  const token = sessionStorage.getItem('token') || null;
  const decoded = token ? decodeToken(token) : null;
  const role = decoded?.role || null;

  const isReadOnly = isAdminRole(role);

  const { breadcrumbName, surveyId, hacId } = location.state || {};

  if (!surveyId || !hacId) {
    return <Navigate to="/admin/main/hac" />;
  }

  return (
    <>
      {token && isAdminRole(role) ? (
        <div className="App">
          <Header />

          <RoleRibbonByRole role={role} navigate={navigate} translate={t} />
          <main>
            <section className="questionary">
              <div className="inner inner-questionary flat-questionay">
                <main className="shared-notes">
                  <BackButton fallbackTo="/admin/main/hac" context={breadcrumbName} />

                  <FlatSurvey
                    surveyId={surveyId}
                    hacId={hacId}
                    isReadOnly={isReadOnly}
                  />
                </main>
              </div>
            </section>
          </main>
        </div>
      ) : (
        <Navigate to="/login" />
      )}
    </>
  );
}

export default AdminSurvey;